'use client';
import {type EventCategory} from '#/cms/schemas/event-category.schema';
import {type Event} from '#/cms/schemas/event.schema';
import {MapInfoWindow} from '#/features/components/MapInfoWindow';
import {MapMarkerSvg} from '#/features/components/MapMarkerSvg';
import {useFileUrl} from '#/features/store/clientEnvStore';
import {usePlaceStore} from '#/features/store/placeStore';
import {useCurrentThemeColors} from '#/features/store/themeColorsStore';
import {useUiStrings} from '#/features/store/uiStrings';
import {latLngToGoogleLatLngLiteral} from '#/utils/geo';
import {Marker} from '@react-google-maps/api';
import Link from 'next/link';
import {type Dispatch, type SetStateAction, useMemo} from 'react';
import {renderToStaticMarkup} from 'react-dom/server';
import classNames from 'classnames';
import Image from 'next/image';
import {DateBadge} from '#/features/components/DateBadge';
import {SmallText} from '#/features/components/Text';

import {getEventUrl} from '#/utils/seo/urls';

const markerSize = 32;

type Props = {
  event: Event;
  eventCategories: EventCategory[];
  showInfo?: boolean;
  setShowInfo?: Dispatch<SetStateAction<string | null>>;
};

/**
 * Map marker component for an event.
 */
export const EventMapMarker = (props: Props) => {
  const {event, eventCategories, showInfo, setShowInfo} = props;
  const themeColors = useCurrentThemeColors();
  const latLng = useMemo(
    () => (event.coordinate ? latLngToGoogleLatLngLiteral(event.coordinate) : undefined),
    [event.coordinate]
  );

  const iconName = useMemo(() => {
    const primaryEventCategoryId = event.eventCategories?.[0]?.id;
    const primaryEventCategory = eventCategories?.find(eventCategory => eventCategory.id === primaryEventCategoryId);
    const iconName = primaryEventCategory?.pepIcon ?? 'Calendar';
    return iconName;
  }, [event.eventCategories, eventCategories]);

  const icon = useMemo(() => {
    const uriBase = 'data:image/svg+xml,';
    const staticMarkup = renderToStaticMarkup(
      <MapMarkerSvg
        iconName={iconName}
        backgroundColor={themeColors.primary}
        foregroundColor={themeColors.light}
        fringeColor={themeColors.modalBackdrop}
        size={markerSize}
      />
    );
    const encodedMarkup = encodeURIComponent(staticMarkup);
    const icon = `${uriBase}${encodedMarkup}`;
    return icon;
  }, [iconName, themeColors]);

  const handleMarkerClick = () => {
    setShowInfo && setShowInfo(event.id);
  };

  if (!latLng) return null;

  return (
    <>
      <Marker position={latLng} icon={icon} onClick={handleMarkerClick} />
      <MapInfoWindow
        isVisible={showInfo}
        position={latLng}
        markerSize={markerSize}
        onCloseClick={() => setShowInfo && setShowInfo(null)}
        Content={<EventMapCallout event={event} />}
      />
    </>
  );
};

function EventMapCallout({event}: {event: Event}) {
  const {placeMap} = usePlaceStore();

  const subtitle = useMemo(
    () =>
      event.placeIds
        ?.map(placeId => placeMap.get(placeId))
        .filter(Boolean)
        .map(place => place!.title)
        .join(', '),
    [placeMap, event.placeIds]
  );
  const uiStrings = useUiStrings();
  const imageUrl = useFileUrl(event.imageSources?.[0]);
  const imageAlt = event.imageCaptions?.[0] ?? uiStrings.events.fallbackImageAlt;

  return (
    <Link href={getEventUrl(event)} className="min-w-72">
      <div className={classNames('flex flex-col gap-y-2 xl:p-1')}>
        <div className="relative min-h-32 min-w-32 overflow-hidden rounded-2xl border border-midground2 xl:min-h-44 xl:min-w-64">
          {imageUrl ? (
            <Image className="object-cover" src={imageUrl} alt={imageAlt} fill />
          ) : (
            <div className="absolute inset-0 bg-midground2"></div>
          )}
        </div>
        <div className="flex flex-row">
          <div className="flex flex-row gap-x-2">
            {!!event.startDateTime && <DateBadge startDate={event.startDateTime} endDate={event.endDateTime} />}
            <div className="flex max-w-[14rem] flex-col gap-y-1">
              <p className="line-clamp-1 text-h3 font-bold text-dark">{event.title}</p>
              {!!subtitle && (
                <SmallText color="dark" className="text-clip">
                  {subtitle}
                </SmallText>
              )}
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
}
